import { apiGet, apiPost, apiPut, apiDelete } from "../../api/client";
import { IVehicle, IVehicleRegistryData } from "../../types";

/**
 * REST client for the `vehicles` resource (served under `/api/inventory`).
 * Replaces direct Firestore access so vehicle data is read and written
 * exclusively through the API.
 */

const VEHICLES_PATH = "/api/inventory/vehicle";

/** Serialized vehicle payload sent as a JSON request body (e.g. `Vehicle.data()`). */
type VehiclePayload = Record<string, unknown>;

/** Full vehicle list for a workspace (no pagination — current default). */
export const listVehicles = async (
  token: string,
  workspaceId: string,
): Promise<IVehicle[]> => {
  const envelope = await apiGet(token, VEHICLES_PATH, workspaceId);
  return (envelope?.data as IVehicle[]) ?? [];
};

export const createVehicle = async (
  token: string,
  workspaceId: string,
  vehicle: VehiclePayload,
): Promise<IVehicle> => {
  const envelope = await apiPost(token, VEHICLES_PATH, vehicle, workspaceId);
  return envelope?.data as IVehicle;
};

export const createRegisteredVehicle = async (
  token: string,
  workspaceId: string,
  vehicle: VehiclePayload,
): Promise<IVehicle> => {
  const envelope = await apiPost(
    token,
    `${VEHICLES_PATH}/registered`,
    vehicle,
    workspaceId,
  );
  return envelope?.data?.vehicle as IVehicle;
};

export const updateVehicle = async (
  token: string,
  workspaceId: string,
  vehicleId: string,
  vehicle: VehiclePayload,
): Promise<IVehicle> => {
  const envelope = await apiPut(
    token,
    `${VEHICLES_PATH}/${vehicleId}`,
    vehicle,
    workspaceId,
  );
  return envelope?.data as IVehicle;
};

export const deleteVehicle = async (
  token: string,
  workspaceId: string,
  vehicleId: string,
): Promise<void> => {
  await apiDelete(token, `${VEHICLES_PATH}/${vehicleId}`, workspaceId);
};

export const lookupVehicle = async (
  token: string,
  workspaceId: string,
  registrationNumber: string,
): Promise<IVehicleRegistryData> => {
  const envelope = await apiPost(
    token,
    `${VEHICLES_PATH}/lookup`,
    { registrationNumber },
    workspaceId,
  );
  return envelope?.data as IVehicleRegistryData;
};

export interface VehicleDriverAccessData {
  driverIds: string[];
  drivers: { id: string; name: string; defaultVehicle: boolean }[];
}

export const getVehicleDrivers = async (
  token: string,
  workspaceId: string,
  vehicleId: string,
): Promise<VehicleDriverAccessData> => {
  const result = await apiGet(
    token,
    `${VEHICLES_PATH}/${vehicleId}/drivers`,
    workspaceId,
  );
  return result.data;
};

export const setVehicleDrivers = async (
  token: string,
  workspaceId: string,
  vehicleId: string,
  driverIds: string[],
  expectedDriverIds: string[],
): Promise<VehicleDriverAccessData> => {
  const result = await apiPut(
    token,
    `${VEHICLES_PATH}/${vehicleId}/drivers`,
    { driverIds, expectedDriverIds },
    workspaceId,
  );
  return result.data;
};
